import React from 'react';
import { capitalize, get, isString } from 'lodash';
import { gql } from '@apollo/client';
import { Box } from '@mui/material';
import {
  FactCheckOutlined as FactCheckIcon,
  PrecisionManufacturingOutlined as PrecisionManufacturingIcon,
  DirectionsRailwayOutlined as DirectionsRailwayIcon,
  WarehouseOutlined as WarehouseIcon,
  LocalShippingOutlined as LocalShippingIcon,
  CelebrationOutlined as CelebrationIcon,
  DoNotDisturbAltOutlined as DoNotDisturbAltOutlinedIcon,
  DirectionsBoatOutlined as DirectionsBoatOutlinedIcon,
  OpenInNewOutlined as OpenInNewOutlinedIcon,
  SellOutlined as SellOutlinedIcon,
} from '@mui/icons-material';
import { Button, Chip, Typography } from 'cprime-foundational-components';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advanced from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import PrintChip from 'components/PrintChip/PrintChip';
import TypographyMarked from 'components/TypographyMarked/TypographyMarked';
import TooltipWrapper from 'components/TooltipWrapper/TooltipWrapper';
import { getNumericInlineValue } from './filterFormatters';
import { chipColorMap, info } from './colorMappers';
import { getEnv } from './environment';
import { formatDataTestId, generateTestId } from 'utils/general';
import { getStatusChipColor, getInventoryCategoryChipColor } from './statusUtils';
import theme from '../theme';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advanced);
dayjs.extend(customParseFormat);

const DATE_FORMAT_1 = 'MM/DD/YYYY';

export const getJourneyStepColor = ({ isActive, isComplete, sw_status }) => {
  if (sw_status?.toLowerCase() === 'eta unavailable') {
    return theme.palette.common.black;
  }
  if (isComplete) {
    return theme.palette.common.black;
  }
  return isActive ? getStatusChipColor(sw_status?.toLowerCase()) : theme.palette.secondary[30];
};

// Format date
export const formatDate = (timestamp, format) => {
  if (!timestamp) {
    return null;
  }

  return dayjs.utc(timestamp).tz(dayjs.tz.guess()).format(format);
};

export const formatDateRaw = (timestamp, format = DATE_FORMAT_1) => {
  if (!timestamp) {
    return null;
  }

  return dayjs.utc(timestamp).format(format);
};

export const getFormattedTimeElapsed = (timestamp) => {
  const MINUTE_IN_MILLISECONDS = 60000;
  const HOUR_IN_MILLISECONDS = MINUTE_IN_MILLISECONDS * 60;
  const DAY_IN_MILLISECONDS = HOUR_IN_MILLISECONDS * 24;

  const now = Date.now();
  const timeElapsed = now - timestamp;

  if (timeElapsed < MINUTE_IN_MILLISECONDS) {
    return `${Math.ceil(timeElapsed / MINUTE_IN_MILLISECONDS)} minute ago`;
  } else if (timeElapsed < HOUR_IN_MILLISECONDS) {
    return `${Math.ceil(timeElapsed / MINUTE_IN_MILLISECONDS)} minutes ago`;
  } else if (timeElapsed < DAY_IN_MILLISECONDS) {
    return `${Math.ceil(timeElapsed / HOUR_IN_MILLISECONDS)} hours ago`;
  }
  return formatDate(timestamp, 'MM/DD/YYYY hh:mm A');
};

// Journey event icon generation

export const generateJourneyEventIcon = ({ eventName, color, position, bottom }) => {
  const styles = { color, position, bottom };
  switch (eventName) {
    case 'Prod Confirm':
      return <FactCheckIcon sx={styles} />;
    case 'Buyoff':
      return <SellOutlinedIcon sx={styles} />;
    case 'Build':
      return <PrecisionManufacturingIcon sx={styles} />;
    case 'Processing':
    case 'VPC Arrival':
    case 'VPC Ready':
      return <WarehouseIcon sx={styles} />;
    case 'Rail Depart':
    case 'Rail Arrive':
    case 'Rail Interchange Arrival':
    case 'Rail Interchange Depart':
    case 'Rail':
      return <DirectionsRailwayIcon sx={styles} />;
    case 'On Truck for Delivery':
    case 'Truck Arrive':
    case 'Truck Depart':
    case 'Truck':
      return <LocalShippingIcon sx={styles} />;
    case 'Arrival at Dealership':
      return <CelebrationIcon sx={styles} />;
    case 'Vessel Depart':
    case 'Vessel Arrive':
    case 'Vessel':
      return <DirectionsBoatOutlinedIcon sx={styles} />;
    default:
      return <DoNotDisturbAltOutlinedIcon sx={styles} />;
  }
};

export const getDistrictName = ({ row }) =>
  row.district === 'Unavailable' || !row.district
    ? 'District N/A'
    : row.vehicleAssignmentIndicator
    ? row.districtFmt
    : `District Pool ${row.district}`;

export const getDealerName = ({ row }) => `${row.dealerCode} - ${row.dealerName}`;

// Data Value Formatter Functions

export const generateBoolValue = (params) => (params.value ? 'Y' : 'N');

export const numAddLeadingZeros = (params) => {
  if (!params?.value) {
    return 'N/A';
  }
  params.value = params.value.toString();
  return params.value.padStart(5, '0');
};

export const deepCopy = (data) => {
  return JSON.parse(JSON.stringify(data));
};

export const getGroupFieldName = (filterObj) => {
  const condKey = Object.keys(filterObj)[0];
  if (isGroupField(condKey, filterObj)) {
    return getGroupFieldName(filterObj[condKey][0]);
  } else {
    return condKey;
  }
};

export const isGroupField = (conditionKey, obj) =>
  ['and', 'or'].includes(conditionKey) && Array.isArray(obj[conditionKey]);

export const getGroupName = ({ data, currentColumns, skipGroupPrefix, suffix = '/' }) => {
  let result = skipGroupPrefix ? '' : 'GROUP: ';
  data.forEach((item, index) => {
    if (index === 3) {
      const hiddenItemCount = data.length - 3;
      result = result.concat(` + ${hiddenItemCount}`);
      return;
    }
    if (index > 3) {
      return;
    }
    const condKey = Object.keys(item)[0];
    const groupField = isGroupField(condKey, item);
    let groupNames;
    if (groupField) {
      groupNames = getGroupName({
        data: item[condKey],
        currentColumns,
        skipGroupPrefix: true,
        suffix,
      });
    }
    {
      const displayFormat = currentColumns?.find(
        (col) => col.field.replace('Fmt', '') === condKey,
      )?.displayFormat;
      result = result.concat(
        index === 0 ? '' : suffix,
        groupField ? groupNames : getNumericInlineValue(get(item, condKey), displayFormat),
      );
    }
  });
  return result;
};

export const formatDataValue = ({ value, fallbackString }) => {
  if (value === null || value === undefined || value === '') {
    return isString(fallbackString) ? fallbackString : 'N/A';
  }
  return value;
};

export const convertToLocaleString = (value) =>
  value !== null && value !== undefined ? value.toLocaleString() : value;

export function base64toBlob(base64Data, contentType = 'application/pdf') {
  const byteCharacters = atob(base64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
}

export const getBlobUrl = (base64Data) => {
  const blob = base64toBlob(base64Data);
  const blobUrl = URL.createObjectURL(blob);
  return blobUrl;
};

export function openPdfInNewTab(base64Data) {
  const blobUrl = getBlobUrl(base64Data);

  // Open the PDF in a new tab
  window.open(blobUrl, '_blank');
}

export const renderBase64PdfLink = ({ name, isHeroAttribute, handleClick, loading, label }) => (
  <Box
    data-testid={generateTestId('VehicleRecord', 'Link', formatDataTestId(label))}
    sx={{
      svg: { width: '1rem' },
      marginTop: '-0.188rem',
    }}
    direction="row"
    alignItems="center"
    mr={1}
  >
    <Button
      disabled={loading}
      endIcon={
        isHeroAttribute && (
          <OpenInNewOutlinedIcon sx={{ color: 'info.main', fontSize: '1rem' }} fontSize="small" />
        )
      }
      onClick={handleClick}
      disableRipple
      sx={{
        minWidth: 0,
        minHeight: 0,
        padding: 0,
        height: '0 !important',
        color: 'info.main',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'transparent',
          textDecoration: 'underline',
        },
      }}
    >
      <Typography variant={isHeroAttribute ? 'body2' : 'body1'}>{name}</Typography>
    </Button>
  </Box>
);

export const formatShippingPriority = (value) => {
  switch (value) {
    case 'Q':
      return 'High';
    case 'S':
      return 'Standard';
    case 'A':
      return 'Low';
    default:
      return '';
  }
};

export const formatNumber = (value, options) => {
  return new Intl.NumberFormat('en-US', options).format(value);
};

export const withInventoryCategoryChip = ({ value, row }) => {
  if (!row.swEtaStatus) {
    return value;
  }
  const color = getInventoryCategoryChipColor(row.swEtaStatus);
  return (
    <Chip
      backgroundColor={color}
      label={value}
      sx={{
        '& .MuiChip-label': {
          textTransform: 'none',
        },
        '& .MuiTypography-root': { fontWeight: 400 },
      }}
    />
  );
};

export const formatRangeValues = (min, max, operator) => {
  switch (operator) {
    case 'between':
      return `>=${min} & <=${max}`;
    case 'ebetween':
      return `>${min} & <${max}`;
    case 'lbetween':
      return `>=${min} & <${max}`;
    case 'rbetween':
      return `>${min} & <=${max}`;
    default:
      return;
  }
};

export const getNetSalesColumnValue = (netSalesValue) =>
  netSalesValue > 0 ? `+${netSalesValue}` : netSalesValue;

export const formatQuery = (query) => {
  const env = getEnv();

  if (['uat', 'prod'].includes(env)) {
    return gql(query?.loc?.source?.body?.replace('awsRequestId\n', ''));
  }
  return query;
};

export const syncAndReorderMenuData = (source, orderArr) => {
  const sourceMap = new Map(source.map((item) => [item.id, item]));

  let orderedPart = orderArr.filter((id) => sourceMap.has(id)).map((id) => sourceMap.get(id));

  let finalResult = [];
  let orderIndex = 0;

  source.forEach((currentItem) => {
    if (!orderArr.includes(currentItem.id)) {
      finalResult.push(currentItem);
    } else {
      finalResult.push(orderedPart[orderIndex]);
      orderIndex++;
    }
  });

  return finalResult;
};

export const parseDateFormat = ({ dateValue, dateFormat, type }) => {
  const format = dateFormat || DATE_FORMAT_1;
  if (type && type.toLowerCase() === 'date' && dateValue !== '' && dateValue !== null) {
    const baseFormats = [
      DATE_FORMAT_1,
      'MM/D/YYYY',
      'M/DD/YYYY',
      'M/D/YYYY',
      'MM/YYYY',
      'M/YYYY',
      'MM/YY',
      'M/YY',
      'MMM/YYYY',
      'MMM YY',
      'YYYYMM',
      'D/M/YYYY',
      'D/MM/YYYY',
      'DD/M/YYYY',
      'DD/MM/YYYY',
      'YYYY/MM/DD',
    ];

    const separators = ['.', '-', ' '];
    const results = [];

    baseFormats.forEach((format) => {
      separators.forEach((sep) => {
        const replaced = format.replace(/\//g, sep);
        results.push(replaced);
      });
    });

    for (const baseFormat of [...baseFormats, ...results]) {
      const parsedDate = dayjs(capitalize(dateValue), baseFormat, true);
      if (parsedDate.isValid()) {
        return { dateValue: parsedDate.format(format) };
      }
    }
  }
  return {
    dateValue,
    error: {
      title: 'Invalid Date Format',
      subtitle: 'The date format you entered is invalid. Please use the format: MM/YYYY or YYYYMM',
    },
  };
};

export const FMTDateToAtDate = (dateFMT) => dayjs(dateFMT).format('MMM D, YYYY [at] h:mm A');

export const formatEventName = (data) =>
  data?.map((item) =>
    item?.EventName === 'Prod. Confirm' ? { ...item, EventName: 'Prod Confirm' } : item,
  );

export const toFirstCapitalLowerRest = (str) =>
  isString(str) ? str?.charAt(0)?.toUpperCase() + str?.slice(1)?.toLowerCase() : str;

export const getLifecycleDetailsChip = ({
  label,
  lifecycleDetails,
  isMobile,
  isPrint,
  isWithTooltip,
  sectionTitle,
  searchContextValues,
}) => {
  return isPrint ? (
    <PrintChip
      label={lifecycleDetails}
      color={chipColorMap?.lifecycleChipText}
      backgroundColor={chipColorMap?.lifecycleChipBackground}
    />
  ) : (
    <TooltipWrapper tooltipText={isWithTooltip ? label : null}>
      <Box>
        <Chip
          label={
            isMobile ? (
              ''
            ) : (
              <TypographyMarked
                dataTestId={generateTestId(
                  'VehicleRecord',
                  'VehicleDetailsFieldValue',
                  formatDataTestId(`${sectionTitle}-${label}`),
                )}
                value={lifecycleDetails}
                fieldId={`${lifecycleDetails}-chip`}
                isPrint={isPrint}
                variant="label1"
                searchContextValues={searchContextValues}
              />
            )
          }
          icon={isMobile ? <>{lifecycleDetails}</> : null}
          color={chipColorMap?.lifecycleChipText}
          backgroundColor={chipColorMap?.lifecycleChipBackground}
        />
      </Box>
    </TooltipWrapper>
  );
};

export const getHeroChip = ({ label, value, isPrint, sectionTitle, searchContextValues }) => {
  return isPrint ? (
    <PrintChip label={value} color={info[10]} backgroundColor={info[100]} />
  ) : (
    <Box pt="0.25rem">
      <Chip
        label={
          <TypographyMarked
            dataTestId={generateTestId(
              'VehicleRecord',
              'VehicleDetailsFieldValue',
              formatDataTestId(`${sectionTitle}-${label}`),
            )}
            value={value}
            fieldId={`${value}-chip`}
            isPrint={isPrint}
            variant="label1"
            searchContextValues={searchContextValues}
          />
        }
        color={info[10]}
        backgroundColor={info[100]}
      />
    </Box>
  );
};

export const getYNValue = (value) => {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  if (value === true) {
    return 'Y';
  }
  if (value === false) {
    return 'N';
  }
  return 'N/A';
};

export const capitalizeFirstLetter = (value) => {
  if (!value) {
    return value;
  }

  if (value.toUpperCase() === 'TMC JAPAN') {
    return 'TMC Japan';
  }

  return value
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const getHoldsTooltipText = (holds) => {
  let text = '';

  if (holds?.qualityHoldCount) {
    text += holds?.qualityHoldCount > 1 ? `QC Hold (${holds?.qualityHoldCount}) \n` : 'QC Hold \n';
  }
  if (holds?.damageHoldCount) {
    text +=
      holds?.damageHoldCount > 1 ? `Damage Hold (${holds?.damageHoldCount}) \n` : 'Damage Hold \n';
  }
  if (holds?.financeHoldCount) {
    text +=
      holds?.financeHoldCount > 1
        ? `Finance Hold (${holds?.financeHoldCount}) \n`
        : 'Finance Hold \n';
  }
  if (holds?.otherHoldCount) {
    text +=
      holds?.otherHoldCount > 1 ? `Other Hold (${holds?.otherHoldCount}) \n` : 'Other Hold \n';
  }
  return text?.trim();
};

export const getJourneyCurrentStepColor = ({ isCurrent, etaStatus }) => {
  if (!isCurrent) {
    return theme.palette.common.black;
  }
  return etaStatus ? getStatusChipColor(etaStatus?.toLowerCase()) : theme.palette.secondary[50];
};

export const addColspanToAttributes = (attributes, labelsToSpan) =>
  attributes.map((attr) =>
    labelsToSpan.includes(attr.label.toLowerCase()) ? { ...attr, colspan: 2 } : attr,
  );
